import { ActivatedRoute, Router } from '@angular/router';
import { AdminService } from 'src/app/services/admin.service';
import { AfterContentInit, Component, OnInit, ViewChild } from '@angular/core';
import { AlertModel } from 'src/app/models/alert.model';
import { AlertNotificationModel } from 'src/app/models/alertNotification.model';
import { AlertService } from 'src/app/services/alert.service';
import { CameraModel, CameraType } from 'src/app/models/camera.model';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { Icons } from 'src/app/models/icons.model';
import { NgbAlert, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SiteModel } from 'src/app/models/site.model';
import { Subscription } from 'rxjs/internal/Subscription';
import { TriggerService } from 'src/app/services/trigger.service';
import { UserModel } from 'src/app/models/user.model';

@Component({
  selector: 'app-admin-alert-detail',
  standalone: false,
  templateUrl: './admin-alert-detail.component.html',
  styleUrls: ['./admin-alert-detail.component.scss'],
})
export class AdminAlertDetailComponent implements OnInit, AfterContentInit {
  // Variables
  adminServiceLoadEvent: Subscription;
  alertMessage = '';
  alertModel: AlertModel = new AlertModel();
  icons = new Icons();
  invalidName: boolean = false;
  isAlertSending: Boolean = false;
  isAlertSendingSuccess: Boolean = false;
  isNew: boolean;
  isReady: boolean;
  selectedSite: SiteModel;
  sites: SiteModel[] = [];
  triggerCameras: CameraModel[] = [];
  users: UserModel[] = [];

  // Ngb Alerts
  @ViewChild('loadingAlert', { static: false }) loadingAlert: NgbAlert;

  /**
   * Get the recipient selected
   * @returns {boolean}
   */
  get recipientSelected(): boolean {
    return !this.isReady || this.users?.filter((u) => u.IsSelected).length > 0;
  }

  constructor(
    private alertService: AlertService,
    private modal: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
    private triggerService: TriggerService,
    public adminService: AdminService,
  ) { }

  ngAfterContentInit() {
    document.scrollingElement.scrollTo(0, 0);
  }

  ngOnInit(): void {
    this.isReady = false;
    this.sites = JSON.parse(JSON.stringify(this.adminService.sites));
    this.users = JSON.parse(JSON.stringify(this.adminService.users));
    this.route.paramMap.subscribe((params) => {
      if (params.has('id')) {
        if (this.adminService.isReady) {
          if (params.get('id') == 'add') {
            this.initNew();
          } else {
            this.initEdit(+params.get('id'));
          }
        } else {
          this.adminServiceLoadEvent = this.adminService.adminServiceLoadedEvent.subscribe(
            () => {
              this.adminServiceLoadEvent.unsubscribe();
              if (params.get('id') == 'add') {
                this.initNew();
              } else {
                this.initEdit(+params.get('id'));
              }
            }
          );
        }
      } else {
        this.router.navigate(['admin', 'alerts']);
      }
    });
  }

  /**
   * @returns void
   */
  initNew(): void {
    this.isNew = true;
    this.isReady = true;
  }

  /**
   * @param  {number} alertId
   */
  initEdit(alertId: number): void {
    this.selectedSite = this.sites.find(
      (s) =>
        s.Id ==
        this.adminService.alerts.find((a) => a.Id == alertId).TriggerCamera
          .SiteId
    );

    this.changeSite();

    this.alertModel = {
      ...this.adminService.alerts.find((a) => a.Id == alertId),
    };

    this.users.forEach((user) => {
      if (this.alertModel.Recipients.find((r) => r.Id == user.Id))
        user.IsSelected = true;
      else user.IsSelected = false;
    });

    this.isReady = true;
  }

  /**
   * Handle change site
   * @returns void
   */
  changeSite(): void {
    this.triggerCameras = [
      ...this.selectedSite.Cameras.filter(
        (c) => c.CameraType == CameraType.Thermal
      ),
    ];
  }

  /**
   * Select all recipient
   * @returns void
   */
  selectAll(): void {
    this.users.forEach((u) => (u.IsSelected = true));
  }

  /**
   * Deselect all recipient
   * @returns void
   */
  selectNone(): void {
    this.users.forEach((u) => (u.IsSelected = false));
  }

  /**
   * Save alert
   * @returns void
   */
  saveAlert(): void {
    this.isReady = false;
    this.alertService
      .checkAlertName(this.alertModel.Name, this.alertModel.Id)
      .then((rtnval) => {
        if (rtnval) {
          this.invalidName = false;
          let operation: Promise<AlertModel>;
          this.alertModel.Recipients = this.users.filter((s) => s.IsSelected);

          if (this.isNew) {
            operation = this.alertService.addAlert(this.alertModel);
          } else {
            operation = this.alertService.updateAlert(this.alertModel);
            console.log(this.alertModel);
          }

          operation
            .then(() => {
              this.router.navigate(['/admin', 'alerts']);
            })
            .catch((err) => {
              console.error(err);
            });
        } else {
          this.invalidName = true;
        }
      })
      .finally(() => {
        this.isReady = true;
      });
  }

  /**
   * Delete Alert
   * @returns void
   */
  deleteAlert(): void {
    const confirm = this.modal.open(ConfirmDialogComponent, {
      keyboard: true,
      centered: true,
    });

    confirm.componentInstance.dialogTitle = 'Delete Alert';
    confirm.componentInstance.dialogMessage =
      'Are you sure you want to delete this Alert?';

    confirm.result
      .then(() => {
        this.alertService
          .deleteAlert(this.alertModel.Id)
          .then(() => {
            this.adminService.reloadService();
            this.router.navigate(['/admin', 'alerts']);
          })
          .catch((err) => console.error(err));
      })
      .catch(() => console.log('canceled'));
  }

  /**
   * Test Email and SMS Alert notification
   * @returns void
   */
  testAlert() {
    // Show the alert sending
    this.isAlertSending = true;

    // Set the alert message
    this.alertMessage = "Sending Notifications..."

    // Start to send email and sms
    this.triggerService
      .alertTest(this.alertModel.Id)
      .then((alertNotification: AlertNotificationModel) => {
        // Hide the Sending Alert Display
        this.isAlertSending = false;

        if (alertNotification.IsEmailSentSuccessful && alertNotification.IsSmsSentSuccessful) {
          // Show success message
          this.isAlertSendingSuccess = true;

          //
          this.alertMessage = "Notification send success"

          // Close the alert after some seconds
          setTimeout(() => {
            this.isAlertSendingSuccess = false;
          }, 5000);
        }
      })
      .catch((err) => console.error(err));
  }

  clearPeerCamera(): void {
    this.alertModel.PeerId = null;
  }
}
